import * as i1 from '@angular/common';
import { CommonModule, NgClass, NgIf } from '@angular/common';
import * as i0 from '@angular/core';
import { EventEmitter, Output, Input, Component, forwardRef, ViewEncapsulation } from '@angular/core';
import { Validators, NG_VALUE_ACCESSOR, NG_VALIDATORS } from '@angular/forms';
import { range } from '@engineering11/utility';

class E11ScoreButtonComponent {
    constructor() {
        this.active = false;
        this.disabled = false;
        this.loading = false;
        this.onSelect = new EventEmitter();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11ScoreButtonComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: E11ScoreButtonComponent, isStandalone: true, selector: "e11-score-button", inputs: { active: "active", disabled: "disabled", loading: "loading", score: "score" }, outputs: { onSelect: "onSelect" }, ngImport: i0, template: `
    <button
      type="button"
      [ngClass]="{
        'e11-bg-skin-primary-accent e11-text-skin-white': active && !loading,
        'e11-bg-white e11-border e11-border-skin-grey e11-text-skin-grey hover:e11-text-skin-primary-accent hover:e11-bg-skin-primary-accent/10 hover:e11-border-skin-primary-accent':
          !active && !loading,
        'e11-cursor-not-allowed e11-opacity-50': disabled && !loading,
        'e11-skeleton e11-cursor-wait e11-text-transparent': loading,
      }"
      class="e11-w-[69px] e11-h-11 e11-my-1 e11-text-xl e11-font-bold e11-rounded-lg e11-transition-all"
      [disabled]="disabled || loading"
      (click)="onSelect.emit(score)"
    >
      {{ score }}
    </button>
  `, isInline: true, dependencies: [{ kind: "ngmodule", type: CommonModule }, { kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11ScoreButtonComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'e11-score-button',
                    standalone: true,
                    imports: [CommonModule],
                    template: `
    <button
      type="button"
      [ngClass]="{
        'e11-bg-skin-primary-accent e11-text-skin-white': active && !loading,
        'e11-bg-white e11-border e11-border-skin-grey e11-text-skin-grey hover:e11-text-skin-primary-accent hover:e11-bg-skin-primary-accent/10 hover:e11-border-skin-primary-accent':
          !active && !loading,
        'e11-cursor-not-allowed e11-opacity-50': disabled && !loading,
        'e11-skeleton e11-cursor-wait e11-text-transparent': loading,
      }"
      class="e11-w-[69px] e11-h-11 e11-my-1 e11-text-xl e11-font-bold e11-rounded-lg e11-transition-all"
      [disabled]="disabled || loading"
      (click)="onSelect.emit(score)"
    >
      {{ score }}
    </button>
  `,
                }]
        }], propDecorators: { active: [{
                type: Input
            }], disabled: [{
                type: Input
            }], loading: [{
                type: Input
            }], score: [{
                type: Input,
                args: [{ required: true }]
            }], onSelect: [{
                type: Output
            }] } });

class E11ScoreLabelComponent {
    constructor() {
        this.hasError = false;
        this.loading = false;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11ScoreLabelComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: E11ScoreLabelComponent, isStandalone: true, selector: "e11-score-label", inputs: { hasError: "hasError", labelText: "labelText", labelClass: "labelClass", loading: "loading" }, ngImport: i0, template: ` <span
    [ngClass]="{
      'e11-text-skin-error': hasError && !loading,
      'e11-skeleton e11-cursor-wait e11-text-transparent e11-border-transparent': loading
    }"
    class="e11-w-full e11-text-skin-black e11-text-lg e11-mb-0.5 {{ labelClass }}"
  >
    {{ labelText }}
  </span>`, isInline: true, dependencies: [{ kind: "directive", type: NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11ScoreLabelComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'e11-score-label',
                    standalone: true,
                    imports: [NgClass],
                    template: ` <span
    [ngClass]="{
      'e11-text-skin-error': hasError && !loading,
      'e11-skeleton e11-cursor-wait e11-text-transparent e11-border-transparent': loading
    }"
    class="e11-w-full e11-text-skin-black e11-text-lg e11-mb-0.5 {{ labelClass }}"
  >
    {{ labelText }}
  </span>`,
                }]
        }], propDecorators: { hasError: [{
                type: Input
            }], labelText: [{
                type: Input,
                args: [{ required: true }]
            }], labelClass: [{
                type: Input
            }], loading: [{
                type: Input
            }] } });

class E11SurveyScoreComponent {
    constructor() {
        this.emptyLabel = 'No scores available';
        this.disabled = false;
        this.required = false;
        this.hasError = false;
        this.loading = false;
        this.containerClass = '';
        this.inputClass = '';
        this.labelClass = '';
        this.valueChange = new EventEmitter();
        this.selectedScore = null;
        this.range = range;
    }
    set question(value) {
        this._question = this.calculateScoreQuestion(value);
    }
    set defaultValues(value) {
        this.selectedScore = value;
    }
    ngOnInit() {
        this.checkRequired();
    }
    checkRequired() {
        if (this.parentForm && this.formControlName) {
            const formControl = this.parentForm.controls[this.formControlName];
            this.required = formControl.hasValidator(Validators.required);
        }
    }
    selectScore(score) {
        this.selectedScore = score;
        this.handleValueChange(this.selectedScore);
    }
    handleValueChange(selectedOptions) {
        if (this.onTouched)
            this.onTouched();
        if (this.onChange)
            this.onChange(selectedOptions);
        this.valueChange.emit(selectedOptions);
        if (this.required && !selectedOptions && !this.hasError) {
            this.hasError = true;
        }
        else if (selectedOptions && this.hasError) {
            this.hasError = false;
        }
    }
    writeValue(obj) {
        if (this.isNumber(obj))
            this.selectedScore = obj;
        else
            this.selectedScore = null;
    }
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    setDisabledState(isDisabled) {
        this.disabled = this.disabled || isDisabled;
    }
    validate(control) {
        if (control.hasValidator(Validators.required) && (control.value?.length == 0 || control.value === null)) {
            this.hasError = this.hasError ? true : control.touched;
            return { required: true };
        }
        this.hasError = false;
        return null;
    }
    isNumber(value) {
        return value && typeof value == 'number';
    }
    calculateScoreQuestion(value) {
        if (!value)
            return;
        return {
            question: value.question,
            scores: Array.from({ length: value.high - value.low + 1 }, (_, i) => i + value.low),
            labelLow: value.labelLow,
            labelHigh: value.labelHigh,
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11SurveyScoreComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "18.2.13", type: E11SurveyScoreComponent, isStandalone: true, selector: "e11-survey-score", inputs: { question: "question", emptyLabel: "emptyLabel", parentForm: "parentForm", formControlName: "formControlName", disabled: "disabled", required: "required", hasError: "hasError", loading: "loading", containerClass: "containerClass", inputClass: "inputClass", labelClass: "labelClass", defaultValues: "defaultValues" }, outputs: { valueChange: "valueChange" }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => E11SurveyScoreComponent),
                multi: true,
            },
            {
                provide: NG_VALIDATORS,
                useExisting: forwardRef(() => E11SurveyScoreComponent),
                multi: true,
            },
        ], ngImport: i0, template: `
    <div class="e11-flex e11-justify-start {{ containerClass }}">
      <div class="e11-flex e11-flex-col e11-items-start">
        <!-- LABEL TEXT -->
        <ng-container *ngIf="_question && !loading">
          <e11-score-label [labelText]="_question.question" [hasError]="hasError" [labelClass]="labelClass"></e11-score-label>
        </ng-container>
        <!-- LOADING LABEL PLACEHOLDER -->
        <ng-container *ngIf="loading">
          <e11-score-label labelText="transparent text" [labelClass]="labelClass" [loading]="true"></e11-score-label>
        </ng-container>
        <div
          [ngClass]="{
          'e11-bg-skin-error/20': hasError,
          'e11-bg-skin-grey/10': !hasError,
        }"
          class="e11-flex e11-flex-wrap e11-gap-x-2 e11-rounded-lg e11-px-4 e11-py-3 e11-mb-1 {{ inputClass }}"
        >
          <!-- OPTIONS LIST -->
          <ng-container *ngIf="_question && !loading">
            @for (score of _question.scores; track score) {
            <e11-score-button
              [score]="score"
              [disabled]="disabled"
              [active]="selectedScore === score"
              [loading]="loading"
              (onSelect)="selectScore(score)"
            >
              {{ score }}
            </e11-score-button>
            } @empty {
            <div class="e11-flex e11-gap-2 e11-h-full e11-items-center">
              <span class="material-icons">error</span>
              <span>{{ emptyLabel }}</span>
            </div>
            }
          </ng-container>

          <!-- LOADING PLACEHOLDER -->
          <ng-container *ngIf="loading">
            @for (item of range(10); track item) {
            <e11-score-button [loading]="true" [score]="item"></e11-score-button>
            }
          </ng-container>
        </div>

        <!-- FOOTER LABELs -->
        <div
          *ngIf="_question.scores?.length > 0"
          class="e11-flex e11-justify-between e11-w-full e11-text-md"
          [ngClass]="{ 'e11-text-skin-grey': !hasError, 'e11-text-skin-error': hasError }"
        >
          <span [ngClass]="{ 'e11-skeleton e11-w-16 e11-h-6': loading }">{{ !loading ? _question.labelLow : '' }}</span>
          <span [ngClass]="{ 'e11-skeleton e11-w-16 e11-h-6': loading }">{{ !loading ? _question.labelHigh : '' }}</span>
        </div>
      </div>
    </div>
  `, isInline: true, dependencies: [{ kind: "ngmodule", type: CommonModule }, { kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: E11ScoreButtonComponent, selector: "e11-score-button", inputs: ["active", "disabled", "loading", "score"], outputs: ["onSelect"] }, { kind: "component", type: E11ScoreLabelComponent, selector: "e11-score-label", inputs: ["hasError", "labelText", "labelClass", "loading"] }], encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11SurveyScoreComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'e11-survey-score',
                    standalone: true,
                    imports: [CommonModule, E11ScoreButtonComponent, E11ScoreLabelComponent],
                    template: `
    <div class="e11-flex e11-justify-start {{ containerClass }}">
      <div class="e11-flex e11-flex-col e11-items-start">
        <!-- LABEL TEXT -->
        <ng-container *ngIf="_question && !loading">
          <e11-score-label [labelText]="_question.question" [hasError]="hasError" [labelClass]="labelClass"></e11-score-label>
        </ng-container>
        <!-- LOADING LABEL PLACEHOLDER -->
        <ng-container *ngIf="loading">
          <e11-score-label labelText="transparent text" [labelClass]="labelClass" [loading]="true"></e11-score-label>
        </ng-container>
        <div
          [ngClass]="{
          'e11-bg-skin-error/20': hasError,
          'e11-bg-skin-grey/10': !hasError,
        }"
          class="e11-flex e11-flex-wrap e11-gap-x-2 e11-rounded-lg e11-px-4 e11-py-3 e11-mb-1 {{ inputClass }}"
        >
          <!-- OPTIONS LIST -->
          <ng-container *ngIf="_question && !loading">
            @for (score of _question.scores; track score) {
            <e11-score-button
              [score]="score"
              [disabled]="disabled"
              [active]="selectedScore === score"
              [loading]="loading"
              (onSelect)="selectScore(score)"
            >
              {{ score }}
            </e11-score-button>
            } @empty {
            <div class="e11-flex e11-gap-2 e11-h-full e11-items-center">
              <span class="material-icons">error</span>
              <span>{{ emptyLabel }}</span>
            </div>
            }
          </ng-container>

          <!-- LOADING PLACEHOLDER -->
          <ng-container *ngIf="loading">
            @for (item of range(10); track item) {
            <e11-score-button [loading]="true" [score]="item"></e11-score-button>
            }
          </ng-container>
        </div>

        <!-- FOOTER LABELs -->
        <div
          *ngIf="_question.scores?.length > 0"
          class="e11-flex e11-justify-between e11-w-full e11-text-md"
          [ngClass]="{ 'e11-text-skin-grey': !hasError, 'e11-text-skin-error': hasError }"
        >
          <span [ngClass]="{ 'e11-skeleton e11-w-16 e11-h-6': loading }">{{ !loading ? _question.labelLow : '' }}</span>
          <span [ngClass]="{ 'e11-skeleton e11-w-16 e11-h-6': loading }">{{ !loading ? _question.labelHigh : '' }}</span>
        </div>
      </div>
    </div>
  `,
                    providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => E11SurveyScoreComponent),
                            multi: true,
                        },
                        {
                            provide: NG_VALIDATORS,
                            useExisting: forwardRef(() => E11SurveyScoreComponent),
                            multi: true,
                        },
                    ],
                    encapsulation: ViewEncapsulation.None,
                }]
        }], propDecorators: { question: [{
                type: Input,
                args: [{ required: true }]
            }], emptyLabel: [{
                type: Input
            }], parentForm: [{
                type: Input
            }], formControlName: [{
                type: Input
            }], disabled: [{
                type: Input
            }], required: [{
                type: Input
            }], hasError: [{
                type: Input
            }], loading: [{
                type: Input
            }], containerClass: [{
                type: Input
            }], inputClass: [{
                type: Input
            }], labelClass: [{
                type: Input
            }], defaultValues: [{
                type: Input
            }], valueChange: [{
                type: Output
            }] } });

class E11SelectorButtonComponent {
    constructor() {
        this.active = false;
        this.disabled = false;
        this.loading = false;
        this.multi = false;
        this.onSelect = new EventEmitter();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11SelectorButtonComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: E11SelectorButtonComponent, isStandalone: true, selector: "e11-selector-button", inputs: { active: "active", disabled: "disabled", loading: "loading", multi: "multi", item: "item", size: "size" }, outputs: { onSelect: "onSelect" }, ngImport: i0, template: `
    <button
      type="button"
      [ngClass]="{
        'e11-selector-active': active && !loading,
        'e11-selector-active-horizontal': active && !loading && multi,
        'e11-selector-inactive': !active && !loading,
        'e11-selector-inactive-horizontal': !active && !loading && multi,
        'e11-selector-inactive-vertical': !active && !loading && !multi,
        'e11-selector-disabled': disabled && !loading,
        'e11-selector-md': size === 'md' && multi,
        'e11-selector-sm': size === 'sm' && multi,
        'e11-selector-loading': loading,
      }"
      class="e11-selector-common"
      [disabled]="disabled || loading"
      (click)="onSelect.emit(item.value)"
    >
      {{ item.name }}
    </button>
  `, isInline: true, styles: [".e11-selector-common{min-width:100%;border-radius:.25rem;border-width:1px;--tw-border-opacity: 1;border-color:rgba(var(--color-primary-accent),var(--tw-border-opacity, 1));padding:.5rem;text-align:left;font-size:.875rem;line-height:1.25rem;--tw-text-opacity: 1;color:rgba(var(--color-text-base),var(--tw-text-opacity, 1));transition-property:all;transition-timing-function:cubic-bezier(.4,0,.2,1);transition-duration:.15s}.e11-selector-md{width:180px;padding:5px;text-align:center}.e11-selector-sm{width:140px;padding:2px;text-align:center;font-size:.75rem;line-height:1rem}.e11-selector-loading{background:#ddd;border-radius:4px;border-color:#ddd;animation:pulse 1.5s infinite;cursor:wait;border-color:transparent;color:transparent;--tw-shadow: 0 0 #0000;--tw-shadow-colored: 0 0 #0000;box-shadow:var(--tw-ring-offset-shadow, 0 0 #0000),var(--tw-ring-shadow, 0 0 #0000),var(--tw-shadow)}.e11-selector-loading:hover{--tw-shadow: 0 0 #0000;--tw-shadow-colored: 0 0 #0000;box-shadow:var(--tw-ring-offset-shadow, 0 0 #0000),var(--tw-ring-shadow, 0 0 #0000),var(--tw-shadow)}.e11-selector-active{--tw-border-opacity: 1;border-color:rgba(var(--color-primary-accent),var(--tw-border-opacity, 1));--tw-bg-opacity: 1;background-color:rgba(var(--color-primary-accent),var(--tw-bg-opacity, 1));--tw-text-opacity: 1;color:rgba(var(--color-text-white),var(--tw-text-opacity, 1));--tw-shadow: 0 4px 6px -1px rgb(0 0 0 / .1), 0 2px 4px -2px rgb(0 0 0 / .1);--tw-shadow-colored: 0 4px 6px -1px var(--tw-shadow-color), 0 2px 4px -2px var(--tw-shadow-color);box-shadow:var(--tw-ring-offset-shadow, 0 0 #0000),var(--tw-ring-shadow, 0 0 #0000),var(--tw-shadow)}.e11-selector-inactive{border-color:rgba(var(--color-grey),.9);--tw-bg-opacity: 1;background-color:rgb(255 255 255 / var(--tw-bg-opacity, 1))}.e11-selector-inactive:hover{--tw-border-opacity: 1;border-color:rgba(var(--color-primary-accent),var(--tw-border-opacity, 1));background-color:rgba(var(--color-primary-accent),.1);--tw-shadow: 0 4px 6px -1px rgb(0 0 0 / .1), 0 2px 4px -2px rgb(0 0 0 / .1);--tw-shadow-colored: 0 4px 6px -1px var(--tw-shadow-color), 0 2px 4px -2px var(--tw-shadow-color);box-shadow:var(--tw-ring-offset-shadow, 0 0 #0000),var(--tw-ring-shadow, 0 0 #0000),var(--tw-shadow)}.e11-selector-disabled{cursor:not-allowed;opacity:.5}.e11-selector-inactive-horizontal{--tw-border-opacity: 1;border-color:rgba(var(--color-primary-accent),var(--tw-border-opacity, 1));--tw-text-opacity: 1;color:rgba(var(--color-primary-accent),var(--tw-text-opacity, 1))}.e11-selector-inactive-vertical{border-color:rgba(var(--color-grey),.9);--tw-text-opacity: 1;color:rgba(var(--color-text-base),var(--tw-text-opacity, 1))}.e11-selector-active-horizontal:hover{--tw-border-opacity: 1;border-color:rgba(var(--color-secondary),var(--tw-border-opacity, 1));--tw-bg-opacity: 1;background-color:rgba(var(--color-secondary),var(--tw-bg-opacity, 1))}\n"], dependencies: [{ kind: "directive", type: NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11SelectorButtonComponent, decorators: [{
            type: Component,
            args: [{ selector: 'e11-selector-button', standalone: true, imports: [NgClass], template: `
    <button
      type="button"
      [ngClass]="{
        'e11-selector-active': active && !loading,
        'e11-selector-active-horizontal': active && !loading && multi,
        'e11-selector-inactive': !active && !loading,
        'e11-selector-inactive-horizontal': !active && !loading && multi,
        'e11-selector-inactive-vertical': !active && !loading && !multi,
        'e11-selector-disabled': disabled && !loading,
        'e11-selector-md': size === 'md' && multi,
        'e11-selector-sm': size === 'sm' && multi,
        'e11-selector-loading': loading,
      }"
      class="e11-selector-common"
      [disabled]="disabled || loading"
      (click)="onSelect.emit(item.value)"
    >
      {{ item.name }}
    </button>
  `, styles: [".e11-selector-common{min-width:100%;border-radius:.25rem;border-width:1px;--tw-border-opacity: 1;border-color:rgba(var(--color-primary-accent),var(--tw-border-opacity, 1));padding:.5rem;text-align:left;font-size:.875rem;line-height:1.25rem;--tw-text-opacity: 1;color:rgba(var(--color-text-base),var(--tw-text-opacity, 1));transition-property:all;transition-timing-function:cubic-bezier(.4,0,.2,1);transition-duration:.15s}.e11-selector-md{width:180px;padding:5px;text-align:center}.e11-selector-sm{width:140px;padding:2px;text-align:center;font-size:.75rem;line-height:1rem}.e11-selector-loading{background:#ddd;border-radius:4px;border-color:#ddd;animation:pulse 1.5s infinite;cursor:wait;border-color:transparent;color:transparent;--tw-shadow: 0 0 #0000;--tw-shadow-colored: 0 0 #0000;box-shadow:var(--tw-ring-offset-shadow, 0 0 #0000),var(--tw-ring-shadow, 0 0 #0000),var(--tw-shadow)}.e11-selector-loading:hover{--tw-shadow: 0 0 #0000;--tw-shadow-colored: 0 0 #0000;box-shadow:var(--tw-ring-offset-shadow, 0 0 #0000),var(--tw-ring-shadow, 0 0 #0000),var(--tw-shadow)}.e11-selector-active{--tw-border-opacity: 1;border-color:rgba(var(--color-primary-accent),var(--tw-border-opacity, 1));--tw-bg-opacity: 1;background-color:rgba(var(--color-primary-accent),var(--tw-bg-opacity, 1));--tw-text-opacity: 1;color:rgba(var(--color-text-white),var(--tw-text-opacity, 1));--tw-shadow: 0 4px 6px -1px rgb(0 0 0 / .1), 0 2px 4px -2px rgb(0 0 0 / .1);--tw-shadow-colored: 0 4px 6px -1px var(--tw-shadow-color), 0 2px 4px -2px var(--tw-shadow-color);box-shadow:var(--tw-ring-offset-shadow, 0 0 #0000),var(--tw-ring-shadow, 0 0 #0000),var(--tw-shadow)}.e11-selector-inactive{border-color:rgba(var(--color-grey),.9);--tw-bg-opacity: 1;background-color:rgb(255 255 255 / var(--tw-bg-opacity, 1))}.e11-selector-inactive:hover{--tw-border-opacity: 1;border-color:rgba(var(--color-primary-accent),var(--tw-border-opacity, 1));background-color:rgba(var(--color-primary-accent),.1);--tw-shadow: 0 4px 6px -1px rgb(0 0 0 / .1), 0 2px 4px -2px rgb(0 0 0 / .1);--tw-shadow-colored: 0 4px 6px -1px var(--tw-shadow-color), 0 2px 4px -2px var(--tw-shadow-color);box-shadow:var(--tw-ring-offset-shadow, 0 0 #0000),var(--tw-ring-shadow, 0 0 #0000),var(--tw-shadow)}.e11-selector-disabled{cursor:not-allowed;opacity:.5}.e11-selector-inactive-horizontal{--tw-border-opacity: 1;border-color:rgba(var(--color-primary-accent),var(--tw-border-opacity, 1));--tw-text-opacity: 1;color:rgba(var(--color-primary-accent),var(--tw-text-opacity, 1))}.e11-selector-inactive-vertical{border-color:rgba(var(--color-grey),.9);--tw-text-opacity: 1;color:rgba(var(--color-text-base),var(--tw-text-opacity, 1))}.e11-selector-active-horizontal:hover{--tw-border-opacity: 1;border-color:rgba(var(--color-secondary),var(--tw-border-opacity, 1));--tw-bg-opacity: 1;background-color:rgba(var(--color-secondary),var(--tw-bg-opacity, 1))}\n"] }]
        }], propDecorators: { active: [{
                type: Input
            }], disabled: [{
                type: Input
            }], loading: [{
                type: Input
            }], multi: [{
                type: Input
            }], item: [{
                type: Input,
                args: [{ required: true }]
            }], size: [{
                type: Input
            }], onSelect: [{
                type: Output
            }] } });

class E11SelectorLabelComponent {
    constructor() {
        this.hasError = false;
        this.errorText = 'Please select an answer';
        this.loading = false;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11SelectorLabelComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: E11SelectorLabelComponent, isStandalone: true, selector: "e11-selector-label", inputs: { hasError: "hasError", labelText: "labelText", errorText: "errorText", labelClass: "labelClass", loading: "loading" }, ngImport: i0, template: `
    <div class="e11-flex e11-flex-row e11-justify-between e11-min-w-full">
      <div
        [ngClass]="{
                'e11-text-skin-error': hasError && !loading,
                'e11-skeleton e11-cursor-wait e11-text-transparent e11-border-transparent e11-shadow-none hover:e11-shadow-none': loading,
              }"
        class="e11-font-bold e11-text-base e11-mb-2 {{ labelClass }}"
      >
        {{ labelText }}
      </div>
      <div *ngIf="hasError" class="e11-flex e11-items-center e11-pointer-events-none e11-text-skin-error">
        <span>{{ errorText }}</span>
      </div>
    </div>
  `, isInline: true, dependencies: [{ kind: "directive", type: NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11SelectorLabelComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'e11-selector-label',
                    standalone: true,
                    imports: [NgClass, NgIf],
                    template: `
    <div class="e11-flex e11-flex-row e11-justify-between e11-min-w-full">
      <div
        [ngClass]="{
                'e11-text-skin-error': hasError && !loading,
                'e11-skeleton e11-cursor-wait e11-text-transparent e11-border-transparent e11-shadow-none hover:e11-shadow-none': loading,
              }"
        class="e11-font-bold e11-text-base e11-mb-2 {{ labelClass }}"
      >
        {{ labelText }}
      </div>
      <div *ngIf="hasError" class="e11-flex e11-items-center e11-pointer-events-none e11-text-skin-error">
        <span>{{ errorText }}</span>
      </div>
    </div>
  `,
                }]
        }], propDecorators: { hasError: [{
                type: Input
            }], labelText: [{
                type: Input,
                args: [{ required: true }]
            }], errorText: [{
                type: Input
            }], labelClass: [{
                type: Input
            }], loading: [{
                type: Input
            }] } });

class E11SurveySelectorComponent {
    constructor() {
        this.valueChange = new EventEmitter();
        this.selectedValues = [];
        this.containerClass = '';
        this.inputClass = '';
        this.labelClass = '';
        this.buttonClass = { common: '', active: '', nonActive: '' };
        this.hasError = false;
        this.required = false;
        this.disabled = false;
        this.loading = false;
        this.multi = false;
        this.size = 'sm';
        this.range = range;
    }
    set defaultValue(value) {
        this.selectedValues = value;
    }
    toggleOption(answer) {
        if (this.selectedValues.includes(answer)) {
            this.selectedValues = this.selectedValues.filter((a) => a !== answer);
        }
        else {
            this.selectedValues = this.multi ? [...this.selectedValues, answer] : [answer];
        }
        this.handleValueChange(this.selectedValues);
    }
    handleValueChange(selectedOptions) {
        if (this.onTouched)
            this.onTouched();
        if (this.onChange)
            this.onChange(selectedOptions);
        this.valueChange.emit(selectedOptions);
        if (this.required && !selectedOptions.length && !this.hasError) {
            this.hasError = true;
        }
        else if (selectedOptions.length > 0 && this.hasError) {
            this.hasError = false;
        }
    }
    ngOnInit() {
        this.checkRequired();
    }
    checkRequired() {
        if (this.parentForm && this.formControlName) {
            const formControl = this.parentForm.controls[this.formControlName];
            this.required = formControl.hasValidator(Validators.required);
        }
    }
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    writeValue(obj) {
        if (this.isArrayOfString(obj))
            this.selectedValues = obj;
        else
            this.selectedValues = [];
    }
    setDisabledState(isDisabled) {
        this.disabled = this.disabled || isDisabled;
    }
    validate(control) {
        if (control.hasValidator(Validators.required) && (control.value?.length == 0 || control.value === null)) {
            this.hasError = this.hasError ? true : control.touched;
            return { required: true };
        }
        this.hasError = false;
        return null;
    }
    isArrayOfString(value) {
        return Array.isArray(value) && value.every((v) => typeof v === 'string');
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11SurveySelectorComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "18.2.13", type: E11SurveySelectorComponent, isStandalone: true, selector: "e11-survey-selector", inputs: { question: "question", containerClass: "containerClass", inputClass: "inputClass", labelClass: "labelClass", buttonClass: "buttonClass", defaultValue: "defaultValue", parentForm: "parentForm", formControlName: "formControlName", hasError: "hasError", required: "required", disabled: "disabled", loading: "loading", multi: "multi", size: "size" }, outputs: { valueChange: "valueChange" }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => E11SurveySelectorComponent),
                multi: true,
            },
            {
                provide: NG_VALIDATORS,
                useExisting: forwardRef(() => E11SurveySelectorComponent),
                multi: true,
            },
        ], ngImport: i0, template: `
    <div class="e11-w-full {{ containerClass }}">
      <!-- LABEL TEXT -->
      <ng-container *ngIf="question && !loading">
        <e11-selector-label [labelText]="question.question" [hasError]="hasError" [labelClass]="labelClass"></e11-selector-label>
      </ng-container>
      <!-- LOADING LABEL PLACEHOLDER -->
      <ng-container *ngIf="loading">
        <e11-selector-label labelText="transparent text" [labelClass]="labelClass" [loading]="true"></e11-selector-label>
      </ng-container>

      <div
        [ngClass]="[
          !multi
            ? 'e11-w-full e11-flex e11-flex-col e11-items-stretch e11-justify-between e11-gap-2'
            : 'e11-w-full e11-flex e11-flex-row e11-flex-wrap e11-gap-2'
        ]"
      >
        <!-- OPTIONS LIST -->
        <ng-container *ngIf="question && !loading">
          @for (item of question.options; track item.value) {
          <e11-selector-button
            [active]="selectedValues.includes(item.value)"
            [disabled]="disabled"
            [loading]="loading"
            [multi]="multi"
            [size]="size"
            [item]="item"
            (onSelect)="toggleOption($event)"
          ></e11-selector-button>
          } @empty {
          <div class="e11-flex e11-gap-2 e11-h-full e11-items-center">
            <span class="material-icons">error</span>
            <span>No options available.</span>
          </div>
          }
        </ng-container>

        <!-- LOADING PLACEHOLDER -->
        <ng-container *ngIf="loading">
          @for (item of range(multi ? 20 : 6); track item) {
          <e11-selector-button
            [loading]="true"
            [multi]="multi"
            [size]="size"
            [item]="{ name: 'transparent text', value: item + '' }"
          ></e11-selector-button>
          }
        </ng-container>
      </div>
    </div>
  `, isInline: true, dependencies: [{ kind: "ngmodule", type: CommonModule }, { kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: E11SelectorButtonComponent, selector: "e11-selector-button", inputs: ["active", "disabled", "loading", "multi", "item", "size"], outputs: ["onSelect"] }, { kind: "component", type: E11SelectorLabelComponent, selector: "e11-selector-label", inputs: ["hasError", "labelText", "errorText", "labelClass", "loading"] }], encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11SurveySelectorComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'e11-survey-selector',
                    standalone: true,
                    imports: [CommonModule, E11SelectorButtonComponent, E11SelectorLabelComponent],
                    template: `
    <div class="e11-w-full {{ containerClass }}">
      <!-- LABEL TEXT -->
      <ng-container *ngIf="question && !loading">
        <e11-selector-label [labelText]="question.question" [hasError]="hasError" [labelClass]="labelClass"></e11-selector-label>
      </ng-container>
      <!-- LOADING LABEL PLACEHOLDER -->
      <ng-container *ngIf="loading">
        <e11-selector-label labelText="transparent text" [labelClass]="labelClass" [loading]="true"></e11-selector-label>
      </ng-container>

      <div
        [ngClass]="[
          !multi
            ? 'e11-w-full e11-flex e11-flex-col e11-items-stretch e11-justify-between e11-gap-2'
            : 'e11-w-full e11-flex e11-flex-row e11-flex-wrap e11-gap-2'
        ]"
      >
        <!-- OPTIONS LIST -->
        <ng-container *ngIf="question && !loading">
          @for (item of question.options; track item.value) {
          <e11-selector-button
            [active]="selectedValues.includes(item.value)"
            [disabled]="disabled"
            [loading]="loading"
            [multi]="multi"
            [size]="size"
            [item]="item"
            (onSelect)="toggleOption($event)"
          ></e11-selector-button>
          } @empty {
          <div class="e11-flex e11-gap-2 e11-h-full e11-items-center">
            <span class="material-icons">error</span>
            <span>No options available.</span>
          </div>
          }
        </ng-container>

        <!-- LOADING PLACEHOLDER -->
        <ng-container *ngIf="loading">
          @for (item of range(multi ? 20 : 6); track item) {
          <e11-selector-button
            [loading]="true"
            [multi]="multi"
            [size]="size"
            [item]="{ name: 'transparent text', value: item + '' }"
          ></e11-selector-button>
          }
        </ng-container>
      </div>
    </div>
  `,
                    providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => E11SurveySelectorComponent),
                            multi: true,
                        },
                        {
                            provide: NG_VALIDATORS,
                            useExisting: forwardRef(() => E11SurveySelectorComponent),
                            multi: true,
                        },
                    ],
                    encapsulation: ViewEncapsulation.None,
                }]
        }], propDecorators: { question: [{
                type: Input,
                args: [{ required: true }]
            }], valueChange: [{
                type: Output
            }], containerClass: [{
                type: Input
            }], inputClass: [{
                type: Input
            }], labelClass: [{
                type: Input
            }], buttonClass: [{
                type: Input
            }], defaultValue: [{
                type: Input
            }], parentForm: [{
                type: Input
            }], formControlName: [{
                type: Input
            }], hasError: [{
                type: Input
            }], required: [{
                type: Input
            }], disabled: [{
                type: Input
            }], loading: [{
                type: Input
            }], multi: [{
                type: Input
            }], size: [{
                type: Input
            }] } });

/**
 * TODO: Determine where copy needs to be inserted (beyond "question")
 * TODO:
 */

/**
 * Generated bundle index. Do not edit.
 */

export { E11SurveyScoreComponent, E11SurveySelectorComponent };
